import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import {
  formatPanelChangeCount,
  formatPanelChangeBadgeTooltip,
  getPanelChangeBadgeBackground,
  getPanelChangeBadgeBackgroundHover,
} from '../helpers/panelChangeGuides';

const useStyles = makeStyles(theme => ({
  layer: {
    position: 'absolute',
    inset: 0,
    zIndex: 26,
    pointerEvents: 'none',
    overflow: 'visible',
  },
  strip: {
    position: 'absolute',
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'transparent',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    outline: 'none',
    // The full-height column stays transparent to the pointer so the chart underneath keeps
    // receiving hover (tooltip/cursor). Only the pill below is interactive.
    pointerEvents: 'none',
    '&:focus-visible $pill': {
      transform: 'scale(1.12)',
      backgroundColor: getPanelChangeBadgeBackgroundHover(theme),
    },
  },
  pillWrap: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  pill: {
    position: 'relative',
    minWidth: 18,
    height: 18,
    padding: '0 6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.text.primary,
    backgroundColor: getPanelChangeBadgeBackground(theme),
    transition: 'transform 120ms ease, background-color 120ms ease',
    cursor: 'pointer',
    // Only the badge captures pointer events, so it stays clickable without blocking the chart.
    pointerEvents: 'auto',
    '&:hover': {
      transform: 'scale(1.12)',
      backgroundColor: getPanelChangeBadgeBackgroundHover(theme),
    },
  },
  pillReadonly: {
    cursor: 'default',
  },
  tooltipMultiline: {
    whiteSpace: 'pre-line',
  },
}));

const PanelChangeGuideStrips = ({strips, onStripClick, period, formatTooltip}) => {
  const classes = useStyles();
  const isClickable = typeof onStripClick === 'function';

  if (!strips?.length) return null;

  return (
    <div className={classes.layer}>
      {strips.map(strip => {
        const tooltipTitle = formatTooltip
          ? formatTooltip(strip)
          : formatPanelChangeBadgeTooltip(strip.count, strip.date, period);

        return (
          <button
            key={strip.date}
            type="button"
            className={classes.strip}
            aria-label={
              isClickable
                ? `View ${strip.count} changed setting${strip.count === 1 ? '' : 's'} for ${
                    strip.date
                  }`
                : `${strip.count} SSP change${strip.count === 1 ? '' : 's'} on ${strip.date}`
            }
            style={{
              left: strip.left,
              top: strip.top,
              width: strip.width,
              height: strip.height,
            }}
            onClick={event => {
              if (!isClickable) return;
              event.stopPropagation();
              const pill = event.currentTarget.querySelector('[data-panel-change-pill]');
              const anchorRect = (pill || event.currentTarget).getBoundingClientRect();
              onStripClick(strip.date, anchorRect);
            }}
          >
            <span className={classes.pillWrap}>
              <Tooltip
                title={
                  typeof tooltipTitle === 'string' ? (
                    <span className={classes.tooltipMultiline}>{tooltipTitle}</span>
                  ) : (
                    tooltipTitle
                  )
                }
                enterDelay={400}
                arrow
              >
                <span
                  data-panel-change-pill
                  className={`${classes.pill} ${isClickable ? '' : classes.pillReadonly}`}
                >
                  {formatPanelChangeCount(strip.count)}
                </span>
              </Tooltip>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default React.memo(PanelChangeGuideStrips);
